import { useApiClient } from '@/composables/api-client'
import { createQuery, createUrl } from '@/utils/pinia/helpers'
import { defaultItemsStoreQuery } from '@/utils/pinia/defaults'
import { createItemsStoreWrapper } from '~/utils/pinia/items-store-setup'
import type { ItemsStoreQuery } from '~/utils/pinia/types'
import type {
  FantasyDayLeaderboardResponse,
  FantasyLeaderboardSection,
  FantasyLeaderboardEntry
} from '~/types/fantasy'
import {
  normalizeLeaderboardEntry,
  normalizeLeaderboardSections
} from '~/utils/fantasy/leaderboard'

const createItemsStore = createItemsStoreWrapper<
  'id',
  FantasyLeaderboardEntry
>()

const storeOptions = {
  path: (_query: ItemsStoreQuery, options: Record<string, unknown>) =>
    `/api/v1/fantasy/season/days/${options.dayId}/leaderboard`,
  idKey: 'id' as const,
  pagination: true,
  paginationType: 'page' as const
}

export const useFantasyDayLeaderboardStore = createItemsStore(
  'fantasy-day-leaderboard',
  storeOptions,
  context => {
    const sections = ref<FantasyLeaderboardSection[]>([])
    const me = ref<FantasyLeaderboardEntry | null>(null)
    const dayId = ref<number | null>(null)

    const applyResponse = (result: FantasyDayLeaderboardResponse) => {
      sections.value = normalizeLeaderboardSections(result.sections)
      me.value = result.me ? normalizeLeaderboardEntry(result.me) : null
    }

    const fetchPage = async (
      id: number,
      requestQuery: ItemsStoreQuery = {}
    ) => {
      const client = await useApiClient()
      const mergedQuery = Object.assign(
        {},
        defaultItemsStoreQuery,
        requestQuery
      )
      const queryToRequest = createQuery(storeOptions, mergedQuery)
      const url = createUrl(storeOptions.path, mergedQuery, { dayId: id })

      return client.get<FantasyDayLeaderboardResponse>(url, {
        query: queryToRequest
      })
    }

    const fetchItems = async (
      id: number,
      requestQuery: ItemsStoreQuery = {}
    ) => {
      dayId.value = id
      context.reset()
      sections.value = []
      me.value = null

      const mergedQuery = Object.assign(
        {},
        defaultItemsStoreQuery,
        { page: 1 },
        requestQuery
      )
      const result = await fetchPage(id, mergedQuery)

      context.items.value = result.items.map(normalizeLeaderboardEntry)
      context.meta.value = result._meta
      context.query.value = mergedQuery
      context.url.value = createUrl(storeOptions.path, mergedQuery, {
        dayId: id
      })
      context.isLastPage.value = result._meta.pageCount < 2

      applyResponse(result)

      return context.items.value
    }

    const fetchMoreItems = async () => {
      if (!dayId.value || context.isLastPage.value || !context.query.value) {
        return context.items.value
      }

      const nextPage = (context.query.value.page ?? 1) + 1
      const mergedQuery = {
        ...context.query.value,
        page: nextPage
      }
      const result = await fetchPage(dayId.value, mergedQuery)

      context.items.value = context.items.value.concat(
        result.items.map(normalizeLeaderboardEntry)
      )
      context.query.value = mergedQuery
      context.meta.value = result._meta
      context.isLastPage.value =
        result._meta.currentPage === result._meta.pageCount

      return context.items.value
    }

    return {
      sections,
      me,
      dayId,
      fetchItems,
      fetchMoreItems
    }
  }
)
